import { useEffect, useMemo, useState } from "react";

import { Page, Container } from "@/ui/Layout/Page";
import { Loading } from "@/ui/Layout/Loading";
import Button from "@/ui/Button";
import { ButtonTypes } from "@/ui/types";
import { getSessionToken } from "@/api/auth";
import { AdminAccount, getAdminAccounts, getAdminMe, loginAs } from "@/api/admin";
import { AccountPanel } from "./AccountPanel";
import { Pill, ago, when } from "./parts";

// ADM-2/3 — the staff-only Admin screen: every Launch OS account, searchable and filtered by
// sign-up date, with a quick "Log in as" on each row and a side panel per person. The server checks
// admin on every call; hiding the menu isn't the guard.

const PAGE_SIZE = 50;
// Every account carries the "10mf" brand (Launch OS ones too), so sign-up date is what separates
// current customers from old 10 Minute Funnels sign-ups.
const ERA_START = "2025-01-01";
type Era = "recent" | "older" | "all";
const ERAS: { id: Era; label: string }[] = [
  { id: "recent", label: "Since 2025" },
  { id: "older", label: "Older" },
  { id: "all", label: "All" },
];
const inEra = (a: AdminAccount, era: Era) =>
  era === "all" ? true : era === "recent" ? String(a.signedUp || "") >= ERA_START : String(a.signedUp || "") < ERA_START;

// the app's global table styles strip padding from the first and last cells; set it on the cells directly
const EDGE_LEFT = { paddingLeft: 20 };
const EDGE_RIGHT = { paddingRight: 20 };

const errorText = (e: unknown) => (e instanceof Error ? e.message : "Something went wrong. Try again.");

export const Accounts = () => {
  const [all, setAll] = useState<AdminAccount[]>([]);
  const [q, setQ] = useState("");
  const [era, setEra] = useState<Era>("recent");
  const [shown, setShown] = useState(PAGE_SIZE);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState("");
  const [opening, setOpening] = useState("");
  const [selected, setSelected] = useState<AdminAccount | null>(null);
  const [isOwner, setIsOwner] = useState(false);
  // SL-4: sort by sign-up date (default), last active, or sign-ins in the last 30 days
  const [sortBy, setSortBy] = useState<"signedUp" | "lastActive" | "signIns30">("signedUp");

  // load every account once; search and the date filter run in the browser so they're instant
  useEffect(() => {
    (async () => {
      try {
        const { accounts } = await getAdminAccounts({ token: getSessionToken() });
        setAll(accounts);
        getAdminMe({ token: getSessionToken() }).then((me) => setIsOwner(me.isOwner)).catch(() => setIsOwner(false));
      } catch (e) {
        setError(errorText(e));
      }
      setIsLoading(false);
    })();
  }, []);

  const counts = useMemo(() => Object.fromEntries(ERAS.map((e) => [e.id, all.filter((a) => inEra(a, e.id)).length])), [all]);

  const filtered = useMemo(() => {
    const words = q.toLowerCase().split(/\s+/).filter(Boolean);
    return all.filter((a) => {
      if (!inEra(a, era)) return false;
      const hay = [a.name, a.email, a.company].filter(Boolean).join(" ").toLowerCase();
      return words.every((w) => hay.includes(w));
    });
  }, [all, q, era]);

  const sorted = useMemo(() => {
    const key = (a: AdminAccount) => (sortBy === "signIns30" ? String(a.signIns30).padStart(6, "0") : String((sortBy === "lastActive" ? a.lastActive : a.signedUp) || ""));
    return [...filtered].sort((a, b) => key(b).localeCompare(key(a)));
  }, [filtered, sortBy]);

  const SortHeader = ({ id, label }: { id: "signedUp" | "lastActive" | "signIns30"; label: string }) => (
    <th className="px-4 py-3">
      <button onClick={() => setSortBy(id)} className={`uppercase tracking-wide ${sortBy === id ? "text-gray-900 dark:text-white" : "hover:text-gray-700"}`}>
        {label} {sortBy === id ? "↓" : ""}
      </button>
    </th>
  );

  const open = async (account: AdminAccount) => {
    if (opening) return;
    setError("");
    setOpening(account.id);
    try {
      await loginAs({ token: getSessionToken(), accountId: account.id });
      // full reload so the menu and apps load for the customer's account
      window.location.href = "/sites";
    } catch (e) {
      setError(errorText(e));
      setOpening("");
    }
  };

  const canLogInAs = (a: AdminAccount) => !a.isAdmin && !a.deactivated;

  return (
    <Page topBar={{ page: [{ label: "Admin" }, { label: "Accounts" }] }}>
      <Container>
        <div className="mx-auto max-w-6xl py-6">
          <div className="mb-4 flex flex-wrap items-end justify-between gap-3">
            <div>
              <h1 className="text-xl font-semibold text-gray-900 dark:text-white">Accounts</h1>
              <p className="text-sm text-gray-500 dark:text-white/60">
                Log in as someone to see exactly what they see. They stay signed in, and every visit is recorded.
              </p>
            </div>
            <input
              autoFocus
              type="search"
              value={q}
              onChange={(e) => {
                setQ(e.target.value);
                setShown(PAGE_SIZE);
              }}
              placeholder="Search name, email or company"
              className="w-full max-w-xs rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm dark:border-white/10 dark:bg-white/5 dark:text-white"
            />
          </div>

          {!isLoading && Boolean(all.length) && (
            <div className="mb-4 flex flex-wrap items-center gap-2 text-sm">
              {ERAS.map((e) => (
                <button
                  key={e.id}
                  onClick={() => {
                    setEra(e.id);
                    setShown(PAGE_SIZE);
                  }}
                  className={`rounded-full px-3 py-1 ring-1 ${era === e.id ? "bg-gray-900 text-white ring-gray-900 dark:bg-white dark:text-gray-900" : "text-gray-600 ring-gray-200 hover:bg-gray-50 dark:text-white/70 dark:ring-white/10 dark:hover:bg-white/5"}`}
                >
                  {e.label} · {counts[e.id]}
                </button>
              ))}
              <span className="ml-auto text-gray-500 dark:text-white/60">{filtered.length} shown</span>
            </div>
          )}

          {error && <div className="mb-4 rounded-lg bg-red-50 px-4 py-3 text-sm text-red-700 dark:bg-red-500/10 dark:text-red-300">{error}</div>}
          {isLoading && <Loading type="gallery" />}

          {!isLoading && !(error && !all.length) && (
            <div className="overflow-x-auto rounded-xl border border-gray-200 dark:border-white/10">
              <table className="min-w-full divide-y divide-gray-200 text-sm dark:divide-white/10">
                <thead className="bg-gray-50 text-left text-xs font-medium uppercase tracking-wide text-gray-500 dark:bg-white/5 dark:text-white/50">
                  <tr>
                    <th className="px-4 py-3" style={EDGE_LEFT}>Account</th>
                    <SortHeader id="signedUp" label="Signed up" />
                    <SortHeader id="lastActive" label="Last active" />
                    <SortHeader id="signIns30" label="Sign-ins (30 days)" />
                    <th className="px-4 py-3">Apps</th>
                    <th className="px-4 py-3" style={EDGE_RIGHT}></th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100 bg-white dark:divide-white/5 dark:bg-transparent">
                  {sorted.slice(0, shown).map((a) => (
                    <tr
                      key={a.id}
                      onClick={() => setSelected(a)}
                      className={`cursor-pointer hover:bg-gray-50 dark:hover:bg-white/5 ${a.deactivated ? "opacity-60" : ""} ${selected?.id === a.id ? "bg-blue-50/60 dark:bg-blue-500/10" : ""}`}
                    >
                      <td className="px-4 py-3" style={EDGE_LEFT}>
                        <div className="flex flex-wrap items-center gap-2 font-medium text-gray-900 dark:text-white">
                          {a.name}
                          {a.isAdmin && <Pill label="Admin" tone="blue" />}
                          {a.deactivated && <Pill label="Deactivated" tone="red" />}
                          {a.teamMemberOf && <Pill label="Team member" tone="amber" />}
                        </div>
                        <div className="text-gray-500 dark:text-white/60">
                          {a.email}
                          {a.company ? ` · ${a.company}` : ""}
                        </div>
                      </td>
                      <td className="whitespace-nowrap px-4 py-3 text-gray-600 dark:text-white/70">{when(a.signedUp)}</td>
                      <td className="whitespace-nowrap px-4 py-3 text-gray-600 dark:text-white/70">{ago(a.lastActive)}</td>
                      <td className="whitespace-nowrap px-4 py-3 text-gray-600 dark:text-white/70">
                        {a.signIns30}
                        {a.failed30 > 0 && <span className="ml-1 text-red-600 dark:text-red-400">· {a.failed30} failed</span>}
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex max-w-xs flex-wrap gap-1">
                          {a.permissions.filter((p) => p !== a.app).map((p) => (
                            <Pill key={p} label={p} />
                          ))}
                        </div>
                      </td>
                      <td className="whitespace-nowrap px-4 py-3 text-right" style={EDGE_RIGHT} onClick={(e) => e.stopPropagation()}>
                        {canLogInAs(a) && (
                          <Button label={opening === a.id ? "Opening…" : "Log in as"} type={ButtonTypes.OUTLINED} onClick={() => open(a)} />
                        )}
                      </td>
                    </tr>
                  ))}
                  {!filtered.length && (
                    <tr>
                      <td colSpan={6} className="px-4 py-10 text-center text-gray-500 dark:text-white/60">
                        No accounts match{q ? ` "${q}"` : ""}.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          )}

          {!isLoading && filtered.length > shown && (
            <div className="mt-4 text-center">
              <Button label={`Show more (${filtered.length - shown} left)`} type={ButtonTypes.SOFT} onClick={() => setShown(shown + PAGE_SIZE)} />
            </div>
          )}
        </div>
      </Container>

      {selected && (
        <AccountPanel
          account={selected}
          onClose={() => setSelected(null)}
          onLogInAs={canLogInAs(selected) ? () => open(selected) : undefined}
          opening={opening === selected.id}
          isOwner={isOwner}
          onChanged={(updated) => {
            setAll((list) => list.map((a) => (a.id === updated.id ? updated : a)));
            setSelected(updated);
          }}
        />
      )}
    </Page>
  );
};
